import React, { useState } from "react";
import { Phone, Mail, MapPin, Send, CheckCircle, AlertCircle } from "lucide-react";
import SEO from "../components/SEO";
import { businessInfo, reassurance, requestTypes } from "../data/content";
import { getBreadcrumbJsonLd } from "../data/jsonld";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Contact() {
  const [form, setForm] = useState({ nom: "", telephone: "", email: "", ville: "", type_demande: "", message: "" });
  const [status, setStatus] = useState(null); // null | "loading" | "success" | "error"
  const [errorMsg, setErrorMsg] = useState("");

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    try {
      const res = await fetch(`${API_URL}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setStatus("success");
        setForm({ nom: "", telephone: "", email: "", ville: "", type_demande: "", message: "" });
      } else {
        setStatus("error");
        if (Array.isArray(data.detail)) {
          const fieldLabels = { nom: "Nom", telephone: "Téléphone", email: "Email", ville: "Ville", type_demande: "Type de demande", message: "Message" };
          const messages = data.detail.map((err) => {
            const field = err.loc?.[err.loc.length - 1];
            const label = fieldLabels[field] || field;
            if (err.type === "string_too_short") return `${label} : ${err.ctx?.min_length || 2} caractères minimum`;
            if (err.type === "string_too_long") return `${label} : trop long`;
            if (err.type === "value_error") return `${label} : format invalide`;
            return `${label} : ${err.msg}`;
          });
          setErrorMsg(messages.join(" | "));
        } else {
          setErrorMsg(data.detail || "Une erreur est survenue.");
        }
      }
    } catch {
      setStatus("error");
      setErrorMsg("Erreur de connexion. Veuillez réessayer.");
    }
  };

  const iconMap = { FileText: Send, Clock: Mail, MapPin, Shield: CheckCircle };

  return (
    <>
      <SEO
        title="Contact - Plombier à Meaux (77) | Devis Gratuit | Pereira Plomberie"
        description="Contactez Pereira Plomberie Chauffage à Meaux. Devis gratuit, réponse rapide. Tél. : 06 73 87 88 61. Email : entpereira@icloud.com."
        jsonLd={getBreadcrumbJsonLd([
          { name: "Accueil", url: "/" },
          { name: "Contact" },
        ])}
      />

      {/* Header */}
      <section className="bg-primary text-white py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-sm font-accent font-medium tracking-widest text-secondary uppercase mb-2">À votre service</p>
          <h1 className="font-heading text-4xl md:text-6xl font-bold uppercase tracking-tight" data-testid="contact-title">
            Contactez-nous
          </h1>
          <p className="text-slate-300 mt-4 max-w-2xl text-lg">
            Une question, un projet, une urgence ? Contactez-nous par téléphone ou remplissez le formulaire ci-dessous pour une réponse rapide.
          </p>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Form */}
            <div className="lg:col-span-2">
              {status === "success" ? (
                <div className="bg-green-50 border border-green-200 p-8 rounded-sm text-center" data-testid="contact-success">
                  <CheckCircle size={48} className="text-green-500 mx-auto mb-4" />
                  <h2 className="font-heading text-2xl font-bold uppercase tracking-tight mb-2">Demande envoyée</h2>
                  <p className="text-slate-600">Merci ! Nous vous recontacterons dans les meilleurs délais.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                  <h2 className="font-heading text-2xl font-bold uppercase tracking-tight mb-2">Demander un devis gratuit</h2>
                  <p className="text-slate-500 text-sm mb-6">Tous les champs sont obligatoires.</p>

                  {status === "error" && (
                    <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 p-3 rounded-sm text-sm" data-testid="contact-error">
                      <AlertCircle size={16} /> {errorMsg}
                    </div>
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="nom" className="block text-sm font-medium mb-1">Nom complet</label>
                      <input id="nom" name="nom" type="text" required value={form.nom} onChange={handleChange} className="flex h-12 w-full rounded-sm border border-slate-300 bg-white px-3 py-2 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-secondary focus:border-transparent" placeholder="Votre nom" data-testid="input-nom" />
                    </div>
                    <div>
                      <label htmlFor="telephone" className="block text-sm font-medium mb-1">Téléphone</label>
                      <input id="telephone" name="telephone" type="tel" required value={form.telephone} onChange={handleChange} className="flex h-12 w-full rounded-sm border border-slate-300 bg-white px-3 py-2 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-secondary focus:border-transparent" placeholder="06 XX XX XX XX" data-testid="input-telephone" />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium mb-1">Email</label>
                      <input id="email" name="email" type="email" required value={form.email} onChange={handleChange} className="flex h-12 w-full rounded-sm border border-slate-300 bg-white px-3 py-2 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-secondary focus:border-transparent" placeholder="votre@email.fr" data-testid="input-email" />
                    </div>
                    <div>
                      <label htmlFor="ville" className="block text-sm font-medium mb-1">Ville</label>
                      <input id="ville" name="ville" type="text" required value={form.ville} onChange={handleChange} className="flex h-12 w-full rounded-sm border border-slate-300 bg-white px-3 py-2 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-secondary focus:border-transparent" placeholder="Meaux, Esbly..." data-testid="input-ville" />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="type_demande" className="block text-sm font-medium mb-1">Type de demande</label>
                    <select id="type_demande" name="type_demande" required value={form.type_demande} onChange={handleChange} className="flex h-12 w-full rounded-sm border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-secondary focus:border-transparent" data-testid="input-type-demande">
                      <option value="">Sélectionnez...</option>
                      {requestTypes.map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium mb-1">Votre message</label>
                    <textarea id="message" name="message" required rows={5} value={form.message} onChange={handleChange} className="flex w-full rounded-sm border border-slate-300 bg-white px-3 py-2 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-secondary focus:border-transparent" placeholder="Décrivez votre besoin..." data-testid="input-message" />
                  </div>

                  <button
                    type="submit"
                    disabled={status === "loading"}
                    className="inline-flex items-center justify-center gap-2 bg-secondary hover:bg-secondary/90 text-white font-bold uppercase tracking-wider py-4 px-8 rounded-sm shadow-md transition-all active:scale-95 disabled:opacity-50 w-full sm:w-auto"
                    data-testid="contact-submit-button"
                  >
                    <Send size={18} />
                    {status === "loading" ? "Envoi en cours..." : "Envoyer ma demande"}
                  </button>
                </form>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <div className="bg-primary text-white p-6 rounded-sm">
                <h3 className="font-heading text-lg font-semibold uppercase tracking-wide mb-4">Coordonnées</h3>
                <div className="space-y-4">
                  <a href={businessInfo.phoneTel} className="flex items-center gap-3 text-secondary hover:text-secondary/80 font-bold" data-testid="contact-sidebar-phone">
                    <Phone size={18} /> {businessInfo.phone}
                  </a>
                  <a href={businessInfo.emailLink} className="flex items-center gap-3 text-slate-300 hover:text-white text-sm" data-testid="contact-sidebar-email">
                    <Mail size={18} /> {businessInfo.email}
                  </a>
                  <div className="flex items-start gap-3 text-slate-400 text-sm">
                    <MapPin size={18} className="flex-shrink-0 mt-0.5" />
                    <span>{businessInfo.address.full}<br />(Interventions à domicile)</span>
                  </div>
                </div>
              </div>

              <div className="bg-muted p-6 rounded-sm border border-slate-200">
                <h3 className="font-heading text-lg font-semibold uppercase tracking-wide mb-4">Nos engagements</h3>
                <div className="space-y-3">
                  {reassurance.map((item, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-secondary/10 flex items-center justify-center rounded-sm">
                        <CheckCircle size={14} className="text-secondary" />
                      </div>
                      <div>
                        <p className="text-sm font-medium">{item.title}</p>
                        <p className="text-xs text-slate-500">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
